import { ProductTypes } from '../model/product/product-type';
import Product from '../model/product/product';
import Windowsticker from '../model/product/windowsticker/windowsticker';
import CustomWindowsticker from '../model/product/windowsticker/custom-windowsticker';
import ProductSize from '../model/product/product-size';
import ProjectInvoice, {
  BoardPartPriceDisplay,
} from '../model/invoice/project-invoice';
import { Project } from '../model/project/project';
import InvoiceHelper from './invoice-helper';
import { OVERRIDES, PriceLine } from '../model/price-line';
import ProductAsset from '../model/product-asset';
import PlacementInvoice from '../model/invoice/placement-invoice';
import DateHelper from './date-helper';
import WindowStickerSize from '../model/product/windowsticker/windowsticker-size';
import ProjectHelper from './project-helper';
import Board, { MaterialChoices } from '../model/product/board/board';
import { CollectionHelper } from './collection-helper';
import BoardPart, {
  PlacementTypeChoices,
} from '../model/product/board/board-part';
import { StringHelper } from './string-helper';
import OtherProduct from '../model/product/other-product/other-product';
import StandardStickerPart from '../model/product/windowsticker/standard-sticker-part';
import StickerPart from '../model/product/windowsticker/sticker-part';
import WindowStickerFactory from '../helpers/product-factory/implementations/windowsticker-factory/windowsticker-factory';
import BoardFactory from '../helpers/product-factory/implementations/board-factory/board-factory';
import OtherProductFactory from '../helpers/product-factory/implementations/other-product-factory/other-product-factory';
import IPBoardFactory from '../helpers/product-factory/implementations/ip-board-factory/ip-board-factory';
import IPBoard from '../model/product/ip-board/ip-board';
import IPBoardPart from '../model/product/ip-board/ip-board-part';
import { IPBoardPlacementTypeChoices } from '../model/product/ip-board/ip-board-placement-type';
import { GeneralHelper } from './general-helper';
import IPBoardPlacement from '../model/product/ip-board/ip-board-placement';
import { ColorChoices } from '../model/color';
import { CLIENT_WINDOW_STICKER_TYPE } from '../model/product/windowsticker/client-windowsticker';
import ProductPhoneNumber from '../model/product/product-phone-number';
import { ClientPendingDetailProduct } from '../model/project/client-pending-detail-product';
import { BoardPartFormHelper } from '../shared/project-components/board-form/board-part-form/board-part-form-helper';

export default class ProductHelper {
  private static supportedImageTypes: string[] = ['image/jpeg', 'image/png'];
  private static typeOptions = [
    { label: 'Raamsticker', value: 'WindowSticker' },
    { label: 'Projectbord', value: 'Board' },
    { label: 'Andere', value: 'OtherProduct' },
  ];
  private static designAssets: string[] = [
    'extraDesign',
    'internalDesign',
    'windowDesign',
    'boardDesign',
    'otherDesign',
  ];
  private static designLocationAssets: string[] = [
    'windowDesign',
    'boardDesign',
    'otherDesign',
  ];

  public static readonly DESIGN_INCLUDING_EXTERNAL: string[] = [
    'externalDesign',
    'extraDesign',
    'internalDesign',
    'windowDesign',
    'boardDesign',
    'otherDesign',
  ];

  public static readonly OTHER_TITLE_ID = 'Andere';

  public static readonly TITLE_OPTIONS: TitleOption[] = [
    {
      id: 'Te_koop',
      label: 'Te koop',
    },
    {
      id: 'Te_huur',
      label: 'Te huur',
    },
    {
      id: 'Te_koop_Te_huur',
      label: 'Te koop/Te huur',
    },
    {
      id: 'A_vendre',
      label: 'A vendre',
    },
    {
      id: 'A_louer',
      label: 'A louer',
    },
    {
      id: ProductHelper.OTHER_TITLE_ID,
      label: 'Andere titel',
    },
  ];

  public static readonly SOLD_RENTED_OPTIONS: TitleOption[] = [
    {
      id: 'Verkocht',
      label: 'Verkocht',
    },
    {
      id: 'Verhuurd',
      label: 'Verhuurd',
    },
    {
      id: 'Vendu',
      label: 'Vendu',
    },
    {
      id: 'Loué',
      label: 'Loué',
    },
    {
      id: ProductHelper.OTHER_TITLE_ID,
      label: 'Andere sticker',
    },
  ];

  static getType(type: string, otherProductName?: string) {
    if (type === ProductTypes.WINDOWSTICKER.value) {
      return 'Raamsticker (standaard)';
    } else if (type === ProductTypes.CUSTOMWINDOWSTICKER.value) {
      return 'Raamsticker (op maat)';
    } else if (type === CLIENT_WINDOW_STICKER_TYPE) {
      return 'Raamsticker';
    } else if (type === ProductTypes.BOARD.value) {
      return 'Projectbord';
    } else if (type === ProductTypes.OTHER_PRODUCT.value) {
      return StringHelper.isEmpty(otherProductName)
        ? 'Andere'
        : otherProductName;
    } else if (type === ProductTypes.IP_BOARD.value) {
      return 'IP-bord';
    } else {
      return 'Product';
    }
  }

  static getTypeOf(product: Product): string {
    if (product == null) {
      return null;
    }
    return this.getType(
      product.type,
      product.type === ProductTypes.OTHER_PRODUCT.value
        ? (<OtherProduct>product).otherProductName
        : null
    );
  }

  static isWindowSticker(product: Product): boolean {
    return product.type === ProductTypes.WINDOWSTICKER.value;
  }

  static isCustomWindowSticker(product: Product): boolean {
    return product.type === ProductTypes.CUSTOMWINDOWSTICKER.value;
  }

  static isClientWindowSticker(product: Product): boolean {
    return product.type === CLIENT_WINDOW_STICKER_TYPE;
  }

  static isBoard(product: Product): boolean {
    return product.type === ProductTypes.BOARD.value;
  }

  static isIPBoard(
    product: Product | ClientPendingDetailProduct
  ): product is IPBoard {
    return product.type === ProductTypes.IP_BOARD.value;
  }

  static isOtherProduct(product: Product): boolean {
    return product.type === ProductTypes.OTHER_PRODUCT.value;
  }

  static getAbbreviatedType(type: string, otherProductName?: string) {
    if (type === ProductTypes.WINDOWSTICKER.value) {
      return 'Raamsticker (S)';
    } else if (type === ProductTypes.CUSTOMWINDOWSTICKER.value) {
      return 'Raamsticker (M)';
    } else if (type === ProductTypes.BOARD.value) {
      return 'Projectbord';
    } else if (type === ProductTypes.IP_BOARD.value) {
      return 'IP-bord';
    } else if (type === ProductTypes.OTHER_PRODUCT.value) {
      return StringHelper.isEmpty(otherProductName)
        ? 'Andere'
        : StringHelper.shortenText(otherProductName, 20);
    } else {
      return 'Product';
    }
  }

  static getSelectedTitleOption(title: string): TitleOption {
    if (StringHelper.isEmpty(title)) {
      return null;
    }
    const seek = this.TITLE_OPTIONS.find((o) => o.label === title);
    return seek == null
      ? this.TITLE_OPTIONS[this.TITLE_OPTIONS.length - 1]
      : seek;
  }

  static getPrice(
    product: Product,
    invoice?: ProjectInvoice,
    project?: Project
  ): number {
    if (product != null) {
      if (product.type === ProductTypes.WINDOWSTICKER.value) {
        let windowsticker = <Windowsticker>product;
        return this.getWindowStickerPrice(windowsticker, invoice, project);
      } else if (product.type === ProductTypes.CUSTOMWINDOWSTICKER.value) {
        let customwindowsticker = <CustomWindowsticker>product;
        return this.getCustomWindowStickerPrice(
          customwindowsticker,
          invoice,
          project
        );
      } else if (product.type === ProductTypes.BOARD.value) {
        let board = <Board>product;
        return this.getBoardPrice(board, invoice, project);
      } else if (product.type === ProductTypes.OTHER_PRODUCT.value) {
        let otherProduct = <OtherProduct>product;
        return this.getOtherProductPrice(otherProduct, invoice, project);
      } else {
        return 0;
      }
    }
    return 0;
  }

  static getWindowStickerPrice(
    product: Windowsticker,
    invoice?: ProjectInvoice,
    project?: Project
  ): number {
    let result = 0;
    if (InvoiceHelper.hasAdjustedProductPrice(invoice, product)) {
      for (let line of invoice.priceLines) {
        if (
          line.placementLine &&
          line.product != null &&
          line.product.id === product.id
        ) {
          result = Number(line.price);
        }
      }
    } else {
      if (
        product.standardStickerParts != null &&
        product.standardStickerParts.length === 1 &&
        product.standardStickerParts[0].productSize != null
      ) {
        result =
          this.computeSum(product.standardStickerParts[0].productSize) /
          this.countInvolvedCompanies(project);
      }
    }
    return result;
  }

  static countInvolvedCompanies(project: Project) {
    if (project != null) {
      return ProjectHelper.getInvolvedCompanies(project).length;
    }
    return 1;
  }

  static getCustomWindowStickerPrice(
    product: CustomWindowsticker,
    invoice?: ProjectInvoice,
    project?: Project
  ): number {
    let result = 0;
    if (
      invoice != null &&
      invoice.type === 'PlacementInvoice' &&
      (<PlacementInvoice>invoice).customProductInvoicePrices != null
    ) {
      let customProductInvoicePrice = (<PlacementInvoice>(
        invoice
      )).customProductInvoicePrices.find(
        (price) => price.product.id === product.id
      );
      if (customProductInvoicePrice != null) {
        result =
          Number(customProductInvoicePrice.productionPrice) +
          Number(customProductInvoicePrice.placementPrice);
      }
    }

    return result;
  }

  static getBoardPrice(
    board: Board,
    invoice: ProjectInvoice,
    project: Project
  ): number {
    if (!CollectionHelper.isPopulated(invoice.priceLines)) {
      return 0;
    }
    return !CollectionHelper.isPopulated(board.boardParts)
      ? 0
      : board.boardParts
          .map((bp) => this.getBoardPartPrice(bp, invoice))
          .reduce((o1, o2) => o1 + o2);
  }

  static getBoardPartPrice(part: BoardPart, invoice: ProjectInvoice) {
    if (!CollectionHelper.isPopulated(invoice.priceLines)) {
      return 0;
    }
    let sum = 0;
    invoice.priceLines.forEach((pl) => {
      if (
        pl.boardPart != null &&
        pl.boardPart.id === part.id &&
        pl.price != null
      ) {
        if (
          (pl.override === OVERRIDES.DISTANCE_TO_BOTTOM_PRICE.value &&
            part.distanceToBottom != null) ||
          (pl.override === OVERRIDES.PAINTED_POSTS_PRICE.value &&
            !part.paintedPosts) ||
          pl.override === OVERRIDES.REPO_PRICE.value
        ) {
          return;
        }
        sum += Number(pl.price);
      }
    });
    return sum;
  }

  static getBoardPartPriceDisplays(
    board: Board,
    boardIndex: number,
    multipleProducts: boolean,
    part: BoardPart,
    partIndex: number,
    invoice: ProjectInvoice
  ): BoardPartPriceDisplay[] {
    const toReturn: BoardPartPriceDisplay[] = [];
    let placementPriceDisplay: BoardPartPriceDisplay;
    if (!CollectionHelper.isPopulated(invoice.priceLines)) {
      return toReturn;
    }

    const multipleBoardParts =
      board.boardParts != null && board.boardParts.length > 1;

    invoice.priceLines.forEach((pl) => {
      if (
        pl.boardPart != null &&
        pl.boardPart.id === part.id &&
        pl.price != null &&
        pl.override != null &&
        pl.override !== OVERRIDES.REPO_PRICE.value
      ) {
        switch (pl.override) {
          case OVERRIDES.PRODUCTION_PRICE.value:
            toReturn.push({
              title: `Aanmaakprijs${this.getBoardPartTitleSuffix(
                multipleProducts,
                multipleBoardParts,
                boardIndex,
                partIndex
              )}`,
              subtitles: [
                StringHelper.join(
                  [`Projectbord`, this.calculateMaterialDisplay(part)],
                  ' - '
                ),
                StringHelper.join(
                  [
                    this.calculateSizeDisplay(part.height, part.width),
                    part.boardType == null ? null : part.boardType.label,
                  ],
                  ' - '
                ),
              ],
              price: pl.price,
            });
            break;
          case OVERRIDES.CONSTRUCTION_PRICE.value:
          case OVERRIDES.PLACEMENT_PRICE.value:
            if (placementPriceDisplay == null) {
              placementPriceDisplay = {
                title: `Plaatsingsprijs${this.getBoardPartTitleSuffix(
                  multipleProducts,
                  multipleBoardParts,
                  boardIndex,
                  partIndex
                )}`,
                subtitles: [
                  StringHelper.join(
                    [
                      part.boardType == null ? null : part.boardType.label,
                      part.placementType == null
                        ? null
                        : part.placementType.ordinal ===
                          PlacementTypeChoices.OTHER.ordinal
                        ? StringHelper.isEmpty(part.otherPlacementType)
                          ? PlacementTypeChoices.OTHER.label
                          : part.otherPlacementType
                        : part.placementType.label,
                    ],
                    ' - '
                  ),
                ],
                price: pl.price,
              };
              toReturn.push(placementPriceDisplay);
            } else {
              placementPriceDisplay.price = (
                Number(placementPriceDisplay.price) + Number(pl.price)
              ).toString();
            }
            break;
          case OVERRIDES.DISTANCE_TO_BOTTOM_PRICE.value:
            toReturn.push({
              title: `Verhoging${this.getBoardPartTitleSuffix(
                multipleProducts,
                multipleBoardParts,
                boardIndex,
                partIndex
              )}`,
              subtitles: null,
              price: pl.price,
            });
            break;
          case OVERRIDES.PAINTED_POSTS_PRICE.value:
            toReturn.push({
              title: `Geschilderde constructie${this.getBoardPartTitleSuffix(
                multipleProducts,
                multipleBoardParts,
                boardIndex,
                partIndex
              )}`,
              subtitles: null,
              price: pl.price,
            });
            break;
          case OVERRIDES.TRANSPORT_PRICE.value:
            toReturn.push({
              title: `Transportprijs${this.getBoardPartTitleSuffix(
                multipleProducts,
                multipleBoardParts,
                boardIndex,
                partIndex
              )}`,
              subtitles: null,
              price: pl.price,
            });
            break;
          default:
            throw new Error(`unknown price line override ${pl.override}`);
        }
      }
    });

    return toReturn;
  }

  private static getBoardPartTitleSuffix(
    multipleProducts: boolean,
    multipleBoardParts: boolean,
    boardIndex: number,
    boardPartIndex: number
  ): string {
    return `${multipleProducts || multipleBoardParts ? ' ' : ''}${
      multipleProducts ? `Projectbord #${boardIndex + 1}` : ''
    }${multipleProducts && multipleBoardParts ? ' ' : ''}${
      multipleBoardParts ? ` deelbord #${boardPartIndex + 1}` : ''
    }`;
  }

  public static getDefaultPriceTotalSum(
    products: Product[],
    atLeastOneRetailer?: boolean
  ): number {
    return !CollectionHelper.isPopulated(products)
      ? 0
      : products
          .map((p) => {
            if (p.type !== ProductTypes.BOARD.value) {
              return 0;
            }
            const board = <Board>p;
            return !CollectionHelper.isPopulated(board.boardParts)
              ? 0
              : board.boardParts
                  .map((bp) =>
                    bp.signSize == null
                      ? 0
                      : this.getBoardPartSignSizeSubtotal(
                          bp,
                          atLeastOneRetailer
                        )
                  )
                  .reduce((a, b) => a + b, 0);
          })
          .reduce((a, b) => a + b, 0);
  }

  /**
   * Precondition: boardPart.signSize != null
   */
  public static getBoardPartSignSizeSubtotal(
    boardPart: BoardPart,
    isRetailer?: boolean
  ): number {
    if (boardPart.signSize == null) {
      return null;
    }
    const productionPrice = this.getBoardPartProductionRetailPriceIfApplicable(
      boardPart,
      isRetailer
    );
    const placementPrice = this.getBoardPartPlacementRetailPriceIfApplicable(
      boardPart,
      isRetailer
    );

    // Calculate transport price based on format size and retailer status
    const transportPrice = this.shouldIncludeTransportPrice(
      boardPart,
      isRetailer
    )
      ? boardPart.signSize.transportPrice || '0'
      : '0';

    return [
      StringHelper.isEmpty(productionPrice) ? 0 : Number(productionPrice),
      StringHelper.isEmpty(placementPrice) ? 0 : Number(placementPrice),
      StringHelper.isEmpty(transportPrice) ? 0 : Number(transportPrice),
      boardPart.paintedPosts
        ? StringHelper.isEmpty(boardPart.signSize.paintedPostsPrice)
          ? 0
          : Number(boardPart.signSize.paintedPostsPrice)
        : 0,
    ].reduce((a, b) => a + b, 0);
  }

  /**
   * Determines whether transport price should be automatically included based on board format and retailer status.
   * Rules:
   * - Only for NON-retailers (companies where 'Is retailer' is unchecked)
   * - Only for boards larger than 200x200 (not equal to 200x200)
   * - Placement types: plaatsing & weghalen
   */
  private static shouldIncludeTransportPrice(
    boardPart: BoardPart,
    isRetailer?: boolean
  ): boolean {
    if (boardPart.height == null || boardPart.width == null) {
      return false;
    }

    // Only for NON-retailers
    if (isRetailer === true) {
      return false;
    }

    // Only for boards larger than 200x200 (not equal to 200x200)
    if (this.isLargerThan2x2Format(boardPart)) {
      return true;
    }

    return false;
  }

  /**
   * Checks if the board part is larger than 2x2 format (200cm x 200cm)
   */
  private static isLargerThan2x2Format(boardPart: BoardPart): boolean {
    // Consider it larger if either dimension is greater than 200cm
    // or if the total area is greater than 2x2 (40,000 cm²)
    const area = boardPart.height * boardPart.width;
    return area > 40000 || boardPart.height > 200 || boardPart.width > 200;
  }

  /**
   * Precondition: boardPart.signSize != null
   */
  public static getBoardPartProductionRetailPriceIfApplicable(
    boardPart: BoardPart,
    isRetailer?: boolean
  ): string {
    return !isRetailer || boardPart.signSize.productionRetailPrice == null
      ? boardPart.signSize.productionPrice
      : boardPart.signSize.productionRetailPrice;
  }

  /**
   * Precondition: boardPart.signSize != null
   */
  public static getBoardPartPlacementRetailPriceIfApplicable(
    boardPart: BoardPart,
    isRetailer?: boolean
  ): string {
    return !isRetailer || boardPart.signSize.placementRetailPrice == null
      ? boardPart.signSize.placementPrice
      : boardPart.signSize.placementRetailPrice;
  }

  static getOtherProductPrice(
    product: OtherProduct,
    invoice?: ProjectInvoice,
    project?: Project
  ): number {
    let result = 0;
    if (
      invoice != null &&
      invoice.type === 'PlacementInvoice' &&
      (<PlacementInvoice>invoice).customProductInvoicePrices != null
    ) {
      let customProductInvoicePrice = (<PlacementInvoice>(
        invoice
      )).customProductInvoicePrices.find(
        (price) => price.product.id === product.id
      );
      if (customProductInvoicePrice != null) {
        result =
          Number(customProductInvoicePrice.productionPrice) +
          Number(customProductInvoicePrice.placementPrice);
      }
    }

    return result;
  }

  static getPriceLinesOfProductAndInvoiceInvoice(
    productId: number,
    invoice: ProjectInvoice
  ): PriceLine[] {
    let result = [];
    if (invoice != null) {
      if (invoice.priceLines != null) {
        for (let line of invoice.priceLines) {
          if (
            line.product.id === productId &&
            !line.placementLine &&
            line.override == null
          ) {
            if (line.price != null && line.text != null && line.text !== '') {
              result.push(line);
            }
          }
        }
      }
    }
    return result;
  }

  static computeSum(productSize: ProductSize): number {
    if (
      (productSize.productionPrice == null ||
        +productSize.productionPrice == 0) &&
      (productSize.placementPrice == null || +productSize.placementPrice == 0)
    ) {
      return 0;
    }
    if (
      productSize.productionPrice == null ||
      +productSize.productionPrice == 0
    ) {
      return Number(productSize.placementPrice.replace(/,/, '.'));
    }
    if (
      productSize.placementPrice == null ||
      +productSize.placementPrice == 0
    ) {
      return Number(productSize.productionPrice.replace(/,/, '.'));
    }
    let production = <string>productSize.productionPrice;
    let placement = <string>productSize.placementPrice;
    let toFormat =
      Number(production.toString().replace(/,/, '.')) +
      Number(placement.toString().replace(/,/, '.'));
    return toFormat;
  }

  static getFormat(ps: ProductSize): string {
    if (ps.height > ps.width) {
      return 'staand';
    }
    if (ps.height === ps.width) {
      return 'vierkant';
    }
    return 'liggend';
  }

  static getOverviewFormat(product: Product): string {
    switch (product.type) {
      case 'WindowSticker':
        const windowSticker = <Windowsticker>product;
        return CollectionHelper.isPopulated(windowSticker.standardStickerParts)
          ? this.getWindowStickerSize(windowSticker.standardStickerParts[0])
          : null;
      case 'CustomWindowSticker':
        const customWindowSticker = <CustomWindowsticker>product;
        return CollectionHelper.isPopulated(customWindowSticker.stickerParts)
          ? this.getCustomWindowStickerSize(customWindowSticker.stickerParts[0])
          : null;
      case 'Board':
        const board = <Board>product;
        return CollectionHelper.isPopulated(board.boardParts)
          ? this.getBoardSize(board.boardParts[0])
          : null;
      case 'OtherProduct':
        return this.getOtherProductSize(<OtherProduct>product);
      default:
        throw new Error(`${product.type} is not a valid product type`);
    }
  }

  static getProductSizeDisplay(product: Product, withFormat = false): string {
    switch (product.type) {
      case ProductTypes.WINDOWSTICKER.value:
        const windowSticker = <Windowsticker>product;
        const firstStandardStickerPart = CollectionHelper.isPopulated(
          windowSticker.standardStickerParts
        )
          ? windowSticker.standardStickerParts[0]
          : null;
        return firstStandardStickerPart == null
          ? null
          : this.getWindowStickerSize(firstStandardStickerPart, withFormat);
      case ProductTypes.CUSTOMWINDOWSTICKER.value:
        const customWindowSticker = <CustomWindowsticker>product;
        const firstStickerPart = CollectionHelper.isPopulated(
          customWindowSticker.stickerParts
        )
          ? customWindowSticker.stickerParts[0]
          : null;
        return firstStickerPart == null
          ? null
          : this.getCustomWindowStickerSize(firstStickerPart, withFormat);
      case ProductTypes.BOARD.value:
        const board = <Board>product;
        const firstBoardPart = CollectionHelper.isPopulated(board.boardParts)
          ? board.boardParts[0]
          : null;
        return firstBoardPart == null
          ? null
          : this.getBoardSize(firstBoardPart, withFormat);
      case ProductTypes.OTHER_PRODUCT.value:
        return this.getOtherProductSize(<OtherProduct>product, withFormat);
      case ProductTypes.IP_BOARD.value:
        return this.getIPBoardSize(<IPBoard>product);
      default:
        throw new Error(`unrecognized product type: ${product.type}`);
    }
  }

  static getWindowStickerSize(
    standardStickerPart: StandardStickerPart,
    withFormat = false
  ) {
    if (standardStickerPart.productSize != null) {
      let formaat = 'Liggend';
      if (
        standardStickerPart.productSize.height ===
        standardStickerPart.productSize.width
      ) {
        formaat = 'Vierkant';
      } else if (
        standardStickerPart.productSize.height >
        standardStickerPart.productSize.width
      ) {
        formaat = 'Staand';
      }
      return (
        (withFormat ? formaat + ' / ' : '') +
        standardStickerPart.productSize.height +
        ' cm H x ' +
        standardStickerPart.productSize.width +
        ' cm B'
      );
    }
  }

  static getStandardWindowStickerLongestTimeBudget(
    windowSticker: Windowsticker,
    property: string
  ): number[] {
    if (
      windowSticker == null ||
      !CollectionHelper.isPopulated(windowSticker.standardStickerParts)
    ) {
      return null;
    }
    let minutes = -Infinity;
    windowSticker.standardStickerParts.forEach((p) => {
      if (p.productSize != null && p.productSize[property] != null) {
        minutes =
          p.productSize[property] > minutes ? p.productSize[property] : minutes;
      }
    });
    if (minutes === -Infinity) {
      return null;
    }
    return GeneralHelper.getMinutesInHourAndMinutesArray(minutes);
  }

  static getStandardWindowStickerTimeBudgetSum(
    windowSticker: Windowsticker,
    property: string
  ): number[] {
    if (
      windowSticker == null ||
      !CollectionHelper.isPopulated(windowSticker.standardStickerParts)
    ) {
      return null;
    }
    let minutes = null;
    windowSticker.standardStickerParts.forEach((p) => {
      if (p.productSize != null && p.productSize[property] != null) {
        if (minutes == null) {
          minutes = 0;
        }
        minutes += p.productSize[property];
      }
    });
    return minutes == null
      ? null
      : GeneralHelper.getMinutesInHourAndMinutesArray(minutes);
  }

  static getCustomWindowStickerSize(
    stickerPart: StickerPart,
    withFormat = false
  ) {
    let format = 'Onbepaald';
    if (stickerPart.stickerHeight != null && stickerPart.stickerWidth == null) {
      return (
        (withFormat ? format + ' / ' : '') + stickerPart.stickerHeight + ' cm H'
      );
    }
    if (stickerPart.stickerHeight == null && stickerPart.stickerWidth != null) {
      return (
        (withFormat ? format + ' / ' : '') + stickerPart.stickerWidth + ' cm B'
      );
    }
    if (stickerPart.stickerHeight != null && stickerPart.stickerWidth != null) {
      if (stickerPart.stickerHeight === stickerPart.stickerWidth) {
        format = 'Vierkant';
      } else if (stickerPart.stickerHeight > stickerPart.stickerWidth) {
        format = 'Staand';
      } else {
        format = 'Liggend';
      }
      return (
        (withFormat ? format + ' / ' : '') +
        stickerPart.stickerHeight +
        ' cm H x ' +
        stickerPart.stickerWidth +
        ' cm B'
      );
    }
    return withFormat ? format : '';
  }

  static getCustomWindowStickerWindowSize(stickerPart: StickerPart) {
    if (stickerPart.windowHeight != null && stickerPart.windowWidth == null) {
      return stickerPart.windowHeight + ' cm H';
    }
    if (stickerPart.windowHeight == null && stickerPart.windowWidth != null) {
      return stickerPart.windowWidth + ' cm B';
    }
    if (stickerPart.windowHeight != null && stickerPart.windowWidth != null) {
      return (
        stickerPart.windowHeight +
        ' cm H x ' +
        stickerPart.windowWidth +
        ' cm B'
      );
    }
    return null;
  }

  static checkWindowStickerTilingSpecified(windowSticker: Windowsticker) {
    return (
      windowSticker.numTilingHorizontal > 0 ||
      windowSticker.numTilingVertical > 0 ||
      windowSticker.tiling === false
    );
  }

  static getBoardSize(boardPart: BoardPart, withFormat = false) {
    let format = 'Onbepaald';
    if (boardPart.height != null && boardPart.width == null) {
      return (withFormat ? format + ' / ' : '') + boardPart.height + ' cm H';
    }
    if (boardPart.height == null && boardPart.width != null) {
      return (withFormat ? format + ' / ' : '') + boardPart.width + ' cm B';
    }
    if (boardPart.height != null && boardPart.width != null) {
      if (boardPart.height === boardPart.width) {
        format = 'Vierkant';
      } else if (boardPart.height > boardPart.width) {
        format = 'Staand';
      } else {
        format = 'Liggend';
      }
      return (
        (withFormat ? format + ' / ' : '') +
        boardPart.height +
        ' cm H x ' +
        boardPart.width +
        ' cm B'
      );
    }
    return withFormat ? format : '';
  }

  static getOtherProductSize(otherProduct: OtherProduct, withFormat = false) {
    let format = 'Onbepaald';
    if (otherProduct.height != null && otherProduct.width == null) {
      return (withFormat ? format + ' / ' : '') + otherProduct.height + ' cm H';
    }
    if (otherProduct.height == null && otherProduct.width != null) {
      return (withFormat ? format + ' / ' : '') + otherProduct.width + ' cm B';
    }
    if (otherProduct.height != null && otherProduct.width != null) {
      if (otherProduct.height === otherProduct.width) {
        format = 'Vierkant';
      } else if (otherProduct.height > otherProduct.width) {
        format = 'Staand';
      } else {
        format = 'Liggend';
      }
      return (
        (withFormat ? format + ' / ' : '') +
        otherProduct.height +
        ' cm H x ' +
        otherProduct.width +
        ' cm B'
      );
    }
    return withFormat ? format : '';
  }

  static getIPBoardSize(ipBoard: IPBoard) {
    if (ipBoard.boardPart == null) {
      return null;
    }
    if (ipBoard.boardPart.sign != null) {
      return ipBoard.boardPart.sign.signMeasurements == null
        ? null
        : `${ipBoard.boardPart.sign.signMeasurements.height} cm H x ${ipBoard.boardPart.sign.signMeasurements.width} cm B`;
    } else {
      return StringHelper.join(
        [
          ipBoard.boardPart.otherHeight == null
            ? null
            : `${ipBoard.boardPart.otherHeight} cm H`,
          ipBoard.boardPart.otherWidth == null
            ? null
            : `${ipBoard.boardPart.otherWidth} cm B`,
        ],
        ' x '
      );
    }
  }

  static getTemporary(temp): string {
    if (temp) {
      return 'Tijdelijk';
    }
    return 'Permanent';
  }

  static isPhoneNumberValidForDisplay(
    productPhoneNumber: ProductPhoneNumber
  ): boolean {
    return (
      productPhoneNumber != null &&
      !StringHelper.isEmpty(productPhoneNumber.phoneNumbers) &&
      productPhoneNumber.phoneNumbers !== 'Geen'
    );
  }

  static nonArchivedImages(product: Product): ProductAsset[] {
    if (product == null || product.productAssets == null) {
      return null;
    }
    return product.productAssets.filter(
      (pa) =>
        !pa.archived && this.supportedImageTypes.indexOf(pa.asset.type) >= 0
    );
  }

  static canDisplayProductAsset(productAsset: ProductAsset): boolean {
    return (
      !productAsset.archived &&
      this.supportedImageTypes.indexOf(productAsset.asset.type) >= 0
    );
  }

  static getConfirmedDate(product: Product) {
    if (
      product == null ||
      product.productPlacement == null ||
      product.productPlacement.actualPlacementDate == null
    ) {
      return '-';
    }
    return DateHelper.formatDate(product.productPlacement.actualPlacementDate);
  }

  static isOfType(product: Product, type: string): boolean {
    return product != null && product.type != null && product.type === type;
  }

  static productSizeAllowedForProduct(
    product: Product,
    productSize: ProductSize
  ): boolean {
    if (product == null || productSize == null) {
      return false;
    }
    if (product.type === 'WindowSticker') {
      let windowSticker = <Windowsticker>product;
      let windowStickerSize = <WindowStickerSize>productSize;
      if (windowSticker.foil == null) {
        return false;
      }
      return (
        windowStickerSize.material.name === windowSticker.foil.name &&
        windowSticker.foil.ordinal === windowStickerSize.material.ordinal
      );
    }
    return false;
  }

  static getTypeOptions(): any[] {
    return this.typeOptions;
  }

  static invoiceDistanceToBottom(boardPart: BoardPart): boolean {
    return boardPart != null && boardPart.distanceToBottom == null;
  }

  static isDesignAsset(productAsset: ProductAsset) {
    return (
      productAsset != null &&
      this.designAssets.includes(productAsset.formSection)
    );
  }

  static isDesignWithExternalAsset(productAsset: ProductAsset) {
    return (
      productAsset != null &&
      this.DESIGN_INCLUDING_EXTERNAL.includes(productAsset.formSection)
    );
  }

  static isDesignLocationAsset(productAsset: ProductAsset): boolean {
    return (
      productAsset != null &&
      this.designLocationAssets.includes(productAsset.formSection)
    );
  }

  static productHasAssetOfFormSection(
    product: Product,
    formSection: string
  ): boolean {
    return (
      product != null &&
      CollectionHelper.isPopulated(product.productAssets) &&
      product.productAssets.some(
        (pa) => !pa.archived && pa.formSection === formSection
      )
    );
  }

  static calculateStandardStickerPartDisplays(
    windowSticker: Windowsticker
  ): StandardStickerPartDisplay[] {
    return CollectionHelper.isPopulated(windowSticker.standardStickerParts)
      ? windowSticker.standardStickerParts.map((p, i) => {
          return {
            id: p.id,
            indexDisplay: ` #${i + 1}`,
            size: this.getWindowStickerSize(p, true),
            layoutDescription: p.layoutDescription,
          };
        })
      : [];
  }

  static calculateCustomStickerPartDisplays(
    customWindowSticker: CustomWindowsticker
  ): CustomStickerPartDisplay[] {
    return CollectionHelper.isPopulated(customWindowSticker.stickerParts)
      ? customWindowSticker.stickerParts.map((p, i) => {
          return {
            id: p.id,
            indexDisplay: ` #${i + 1}`,
            stickerSize: this.getCustomWindowStickerSize(p, true),
            windowSize: this.getCustomWindowStickerWindowSize(p),
            description: p.description,
          };
        })
      : [];
  }

  static calculateBoardPartDisplays(board: Board): BoardPartDisplay[] {
    return CollectionHelper.isPopulated(board.boardParts)
      ? board.boardParts.map((bp, i) => {
          return this.calculateBoardPartDisplay(bp, i);
        })
      : [];
  }

  static calculateBoardPartDisplay(
    boardPart: BoardPart,
    index: number
  ): BoardPartDisplay {
    return {
      id: boardPart.id,
      indexDisplay: ` #${index + 1}`,
      size: this.getBoardSize(boardPart, true),
      materialDisplay: this.calculateMaterialDisplay(boardPart),
      boardType: boardPart.boardType == null ? null : boardPart.boardType.label,
      placementType:
        boardPart.placementType == null
          ? null
          : this.calculatePlacementTypeDisplay(
              boardPart.placementType.label,
              boardPart.otherPlacementType
            ),
      showDistanceToBottom:
        BoardPartFormHelper.calculateShowDistanceToBottomSelector(boardPart),
      distanceToBottomDisplay: this.calculateDistanceToBottomDisplay(boardPart),
      paintedPostsDisplay: this.calculatePaintedPostsDisplay(boardPart),
      layoutDescription: boardPart.layoutDescription,
    };
  }

  static calculateMaterialDisplay(boardPart: BoardPart): string {
    return boardPart.material == null
      ? null
      : boardPart.material.ordinal === MaterialChoices.UNSPECIFIED.ordinal
      ? StringHelper.isEmpty(boardPart.otherMaterial)
        ? MaterialChoices.UNSPECIFIED.name
        : boardPart.otherMaterial
      : boardPart.material.name;
  }

  static calculatePlacementTypeDisplay(
    value: string,
    otherPlacementType: string
  ): string {
    return value === 'Andere'
      ? StringHelper.isEmpty(otherPlacementType)
        ? 'Andere'
        : otherPlacementType
      : value;
  }

  static calculateDistanceToBottomDisplay(boardPart: BoardPart): string {
    return boardPart.distanceToBottom == null
      ? StringHelper.isEmpty(boardPart.distanceToBottomText)
        ? 'Aangepast'
        : boardPart.distanceToBottomText
      : `${boardPart.distanceToBottom} cm`;
  }

  static calculatePaintedPostsDisplay(boardPart: BoardPart): string {
    return boardPart.paintedPosts
      ? boardPart.paintedPostsValue == null ||
        boardPart.paintedPostsValue.ordinal === ColorChoices.OTHER.ordinal
        ? StringHelper.isEmpty(boardPart.paintedPostsText)
          ? 'Andere kleur'
          : boardPart.paintedPostsText
        : boardPart.paintedPostsValue.label
      : null;
  }

  static calculateTableBoardDisplay(board: Board): void {
    const boardPart = CollectionHelper.isPopulated(board.boardParts)
      ? board.boardParts[0]
      : null;
    if (boardPart != null) {
      board.boardType =
        boardPart.boardType == null ? null : boardPart.boardType.label;
      board.placementType =
        boardPart.placementType == null
          ? null
          : boardPart.placementType.ordinal !== 4
          ? boardPart.placementType.label
          : StringHelper.isEmpty(boardPart.otherPlacementType)
          ? boardPart.placementType.label
          : boardPart.otherPlacementType;
    }
  }

  static calculateSizeDisplay(
    height: number,
    width: number,
    unit = 'cm'
  ): string {
    return StringHelper.join(
      [
        height == null ? '' : `${height}${unit} H`,
        width == null ? '' : `${width}${unit} B`,
      ],
      ' x '
    );
  }

  static productPreventsShowFloorQuestion(p: Product): boolean {
    if (p.type === ProductTypes.BOARD.value) {
      return this.boardPreventsShowFloorQuestion(<Board>p);
    }
    if (p.type === ProductTypes.IP_BOARD.value) {
      return this.ipBoardPreventsShowFloorQuestion(<IPBoard>p);
    }
    return false;
  }

  private static boardPreventsShowFloorQuestion(board: Board): boolean {
    return (
      CollectionHelper.isPopulated(board.boardParts) &&
      board.boardParts.every(
        (bp) =>
          bp.placementType != null &&
          (bp.placementType.ordinal === PlacementTypeChoices.ON_POSTS.ordinal ||
            bp.placementType.ordinal === PlacementTypeChoices.ON_STONES.ordinal)
      )
    );
  }

  private static ipBoardPreventsShowFloorQuestion(ipBoard: IPBoard): boolean {
    return (
      this.ipBoardPartPreventsShowFloorQuestion(ipBoard.boardPart) ||
      this.ipBoardPartPreventsShowFloorQuestion(ipBoard.otherSideBoardPart)
    );
  }

  private static ipBoardPartPreventsShowFloorQuestion(
    ipBoardPart: IPBoardPart
  ): boolean {
    return (
      ipBoardPart != null &&
      ipBoardPart.placementType != null &&
      (ipBoardPart.placementType.ordinal ===
        IPBoardPlacementTypeChoices.ON_POSTS.ordinal ||
        ipBoardPart.placementType.ordinal ===
          IPBoardPlacementTypeChoices.ON_STONES.ordinal)
    );
  }

  static placementFloorRequiredForProduct(p: Product): boolean {
    return this.isIPBoard(p)
      ? this.placementFloorRequiredForIPBoard(<IPBoard>p)
      : false;
  }

  private static placementFloorRequiredForIPBoard(ipBoard: IPBoard): boolean {
    return (
      this.placementFloorRequiredForIPBoardPart(ipBoard.boardPart) ||
      this.placementFloorRequiredForIPBoardPart(ipBoard.otherSideBoardPart)
    );
  }

  private static placementFloorRequiredForIPBoardPart(
    ipBoardPart: IPBoardPart
  ): boolean {
    return (
      ipBoardPart != null &&
      ipBoardPart.placementType != null &&
      (ipBoardPart.placementType.ordinal ===
        IPBoardPlacementTypeChoices.BALCONY.ordinal ||
        ipBoardPart.placementType.ordinal ===
          IPBoardPlacementTypeChoices.STRETCHED.ordinal ||
        ipBoardPart.placementType.ordinal ===
          IPBoardPlacementTypeChoices.WINDOW.ordinal)
    );
  }

  static getIPBoardForPoleColor(project: Project): IPBoard {
    return !CollectionHelper.isPopulated(project.products)
      ? null
      : project.products.find((p) => {
          if (!ProductHelper.isIPBoard(p)) {
            return false;
          }
          const ipBoard = <IPBoard>p;
          return (
            ipBoard.boardPart != null &&
            ipBoard.boardPart.sign == null &&
            ipBoard.boardPart.placementType != null
          );
        });
  }

  static showUndergroundPipeworkQuestionForIPBoard(ipBoard: IPBoard): boolean {
    return (
      this.showUndergroundPipeworkQuestionForIPBoardPart(ipBoard.boardPart) ||
      this.showUndergroundPipeworkQuestionForIPBoardPart(
        ipBoard.otherSideBoardPart
      )
    );
  }

  private static showUndergroundPipeworkQuestionForIPBoardPart(
    ipBoardPart: IPBoardPart
  ): boolean {
    return (
      ipBoardPart != null &&
      ipBoardPart.placementType != null &&
      ipBoardPart.placementType.ordinal ===
        IPBoardPlacementTypeChoices.ON_POSTS.ordinal
    );
  }

  static ipBoardHasArrows(ipBoard: IPBoard): boolean {
    if (ipBoard.productPlacement == null) {
      return false;
    }
    const placement = <IPBoardPlacement>ipBoard.productPlacement;
    return (
      placement.arrows != null &&
      (placement.arrows.amountArrowsLeft != null ||
        placement.arrows.amountArrowsRight != null ||
        !StringHelper.isEmpty(placement.arrows.arrowsDesign) ||
        !StringHelper.isEmpty(placement.arrows.arrowsLocation))
    );
  }

  static doTitleSidesDiffer(ipBoardPart: IPBoardPart): boolean {
    if (
      ipBoardPart.titleAdjustment == null &&
      ipBoardPart.otherTitleAdjustment == null &&
      StringHelper.isEmpty(ipBoardPart.otherTitle) &&
      StringHelper.isEmpty(ipBoardPart.otherSideOtherTitle)
    ) {
      return false;
    }
    if (
      ipBoardPart.titleAdjustment != null &&
      ipBoardPart.otherTitleAdjustment == null
    ) {
      return true;
    }
    if (
      ipBoardPart.titleAdjustment == null &&
      ipBoardPart.otherTitleAdjustment != null
    ) {
      return true;
    }
    if (
      ipBoardPart.titleAdjustment != null &&
      ipBoardPart.otherTitleAdjustment != null
    ) {
      return (
        ipBoardPart.titleAdjustment.id !== ipBoardPart.otherTitleAdjustment.id
      );
    }
    return ipBoardPart.otherTitle !== ipBoardPart.otherSideOtherTitle;
  }

  static doPhoneNumberSidesDiffer(ipBoardPart: IPBoardPart): boolean {
    if (
      ipBoardPart.phoneNumberAdjustment == null &&
      ipBoardPart.otherPhoneNumberAdjustment == null &&
      StringHelper.isEmpty(ipBoardPart.otherPhoneNumber) &&
      StringHelper.isEmpty(ipBoardPart.otherSideOtherPhoneNumber)
    ) {
      return false;
    }
    if (
      ipBoardPart.phoneNumberAdjustment != null &&
      ipBoardPart.otherPhoneNumberAdjustment == null
    ) {
      return true;
    }
    if (
      ipBoardPart.phoneNumberAdjustment == null &&
      ipBoardPart.otherPhoneNumberAdjustment != null
    ) {
      return true;
    }
    if (
      ipBoardPart.phoneNumberAdjustment != null &&
      ipBoardPart.otherPhoneNumberAdjustment != null
    ) {
      return (
        ipBoardPart.phoneNumberAdjustment.id !==
        ipBoardPart.otherPhoneNumberAdjustment.id
      );
    }
    return (
      ipBoardPart.otherPhoneNumber !== ipBoardPart.otherSideOtherPhoneNumber
    );
  }

  static getPlacerNames(products: Product[], sep = ', '): string {
    if (!CollectionHelper.isPopulated(products)) {
      return '';
    }

    const seenPlacer = new Set<number>();
    const names: string[] = [];

    for (const product of products) {
      if (
        product == null ||
        product.productPlacement == null ||
        product.productPlacement.placers == null
      ) {
        continue;
      }
      for (const placer of product.productPlacement.placers) {
        if (
          placer == null ||
          placer.user == null ||
          seenPlacer.has(placer.id)
        ) {
          continue;
        }
        names.push(
          StringHelper.join(
            [
              placer.user.firstName == null
                ? null
                : placer.user.firstName.toString(),
              placer.user.lastName == null
                ? null
                : placer.user.lastName.toString(),
            ],
            ' '
          )
        );
        seenPlacer.add(placer.id);
      }
    }

    if (names.length === 0) {
      return '';
    }
    return StringHelper.join(names, sep);
  }
}

export class StandardStickerPartDisplay {
  id: number;
  indexDisplay: string;
  size: string;
  layoutDescription: string;
}

export class CustomStickerPartDisplay {
  id: number;
  indexDisplay: string;
  windowSize: string;
  stickerSize: string;
  description: string;
}

export class BoardPartDisplay {
  id: number;
  indexDisplay: string;
  size: string;
  materialDisplay: string;
  boardType: string;
  placementType: string;
  distanceToBottomDisplay: string;
  paintedPostsDisplay: string;
  layoutDescription: string;
  showDistanceToBottom?: boolean; // frontend only
}

export interface TitleOption {
  id: string;
  label: string;
}

export const PRODUCT_FACTORIES = {
  WINDOWSTICKER_FACTORY: new WindowStickerFactory(),
  BOARD_FACTORY: new BoardFactory(),
  OTHER_PRODUCT_FACTORY: new OtherProductFactory(),
  IP_BOARD_FACTORY: new IPBoardFactory(),
};
